import { useLayoutEffect, useState } from 'react';
import { ApiResponse } from '@nf/types/api';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { useApiBackendInfo } from '@/hooks/query/use-api-backend-info';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { ApiBackendInfo } from '@/hooks/query/athena/types';
import { WcPromotionSpinHistoryQuery, WcPromotionSpinHistoryResponse } from './types';
import {
	WC_PROMOTION_SPIN_HISTORY_CACHE_MS,
	WC_PROMOTION_SPIN_HISTORY_INITIAL_TAKE,
	WC_PROMOTION_SPIN_HISTORY_MORE_TAKE
} from './constants';

export const fetchWcPromotionSpinHistory = async (
	apiBackendInfo: ApiBackendInfo,
	query: WcPromotionSpinHistoryQuery
): Promise<ApiResponse<WcPromotionSpinHistoryResponse>> => {
	const params = new URLSearchParams({
		skip: String(query.skip),
		take: String(query.take)
	});
	const response = await fetchComposer.getWithBearer(`${apiBackendInfo.url}/spin/history?${params}`)(
		apiBackendInfo.token
	);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return await response.json();
};

export type UseWcPromotionSpinHistoryOptions = {
	/**
	 * 為 true 時，每次訂閱此 hook 的元件掛載會清除 spin history 快取並從第一頁重打 API。
	 * 預設 true（歷程彈窗每次開啟需最新資料）。
	 */
	resetOnMount?: boolean;
};

export const useWcPromotionSpinHistory = (
	options?: UseWcPromotionSpinHistoryOptions
) => {
	const { resetOnMount = true } = options ?? {};
	const { enableWcPromotionLuckySpin } = useLicSettings();
	const apiBackendInfo = useApiBackendInfo();
	const queryClient = useQueryClient();
	/** 避免 resetQueries 與初次 fetch 疊成兩次 API：需 reset 時先擋 query，layout 後再開 */
	const [allowFetchAfterReset, setAllowFetchAfterReset] = useState(() => !resetOnMount);
	const queryEnabled =
		enableWcPromotionLuckySpin && apiBackendInfo.isReady && (!resetOnMount || allowFetchAfterReset);

	const {
		data,
		isLoading,
		isError,
		error,
		refetch,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage
	} = useInfiniteQuery({
		queryKey: [queryKeys.WC_PROMOTION_SPIN_HISTORY, apiBackendInfo.url, apiBackendInfo.token],
		queryFn: async ({ pageParam }) => {
			const pageQuery: WcPromotionSpinHistoryQuery =
				(pageParam as WcPromotionSpinHistoryQuery | undefined) ?? {
					skip: 0,
					take: WC_PROMOTION_SPIN_HISTORY_INITIAL_TAKE
				};
			const res = await fetchWcPromotionSpinHistory(apiBackendInfo, pageQuery);
			if (res.Data == null) {
				throw new Error('Spin history: empty response');
			}
			return res.Data;
		},
		getNextPageParam: (lastPage, allPages) => {
			if (lastPage.hasNextPage !== true) {
				return undefined;
			}
			const loaded = allPages.reduce((sum, page) => sum + page.histories.length, 0);
			return { skip: loaded, take: WC_PROMOTION_SPIN_HISTORY_MORE_TAKE };
		},
		staleTime: WC_PROMOTION_SPIN_HISTORY_CACHE_MS,
		cacheTime: WC_PROMOTION_SPIN_HISTORY_CACHE_MS,
		enabled: queryEnabled
	});

	useLayoutEffect(() => {
		if (!resetOnMount) return;
		queryClient.resetQueries({ queryKey: [queryKeys.WC_PROMOTION_SPIN_HISTORY] });
		setAllowFetchAfterReset(true);
	}, [queryClient, resetOnMount]);

	const histories = data?.pages.flatMap((p) => p.histories) ?? [];
	const totalCount = data?.pages[data.pages.length - 1]?.totalCount;

	return {
		histories,
		totalCount,
		fetchNextPage,
		hasNextPage: Boolean(hasNextPage),
		isFetchingNextPage,
		isLoading,
		isError,
		error,
		refetch
	};
};
